<template>
  <div class="w-full h-full space-y-4">
    <!--
      The "Import users" button and its CSV drawer were removed. The drawer was only ever
      opened by that button, so it is unreachable without it; the markup and the
      onFile/downloadTemplate/importUsers methods are in git history if import returns.
    -->
    <div
      v-if="topLineData.activeUsers != undefined"
      class="grid grid-cols-2 md:grid-cols-3 gap-3"
    >
      <Card class="p-4">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs uppercase tracking-wide text-muted-foreground">Total users (All time)</p>
          <Users class="w-4 h-4 text-muted-foreground" />
        </div>
        <p class="text-2xl font-semibold">{{ topLineData.totalUsers }}</p>
      </Card>
      <Card class="p-4">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs uppercase tracking-wide text-muted-foreground">Active users (60 days)</p>
          <Speech class="w-4 h-4 text-muted-foreground" />
        </div>
        <p class="text-2xl font-semibold">{{ topLineData.activeUsers }}</p>
      </Card>
      <Card class="p-4">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs uppercase tracking-wide text-muted-foreground">Percentage of active users</p>
          <Percent class="w-4 h-4 text-muted-foreground" />
        </div>
        <p class="text-2xl font-semibold">{{ topLineData.totalUsers ? topLineData.percentage.toFixed(2) : '0' }}%</p>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Users</CardTitle>
        <CardDescription>View your enrolled end users</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headers"
          :items="users"
          :loading="loading"
          :error="error"
          @retry="getUsers(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by end user id"
          :search="search"
          @update:search="search = $event"
          @search="getUsers(0, serverOptions.rowsPerPage)"
          @clear-search="onClearSearch"
        >
          <template #item-endUserId="{ endUserId }">
            <CopyId v-if="endUserId" :value="endUserId" :max-width="120" />
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-createdOn="{ createdOn }">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{ createdOn ? niceDateTime(createdOn) : '—' }}</span>
          </template>
          <template #item-action="user">
            <div class="flex justify-end items-center gap-1">
              <Button variant="outline" size="sm" @click="$router.push(`/enrolled-users/users/${user.endUserId || user.id}`)">View user</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
  Button,
  CopyId,
} from '@boogi/ui'
import { Users, Speech, Percent } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'users-list',
  components: {
    DataTable,
    Card, CardHeader, CardTitle, CardDescription, CardContent,
    Button,
    CopyId,
    Users, Speech, Percent,
  },
  data() {
    return {
      search: '',
      users: [],
      loading: true,
      error: false,
      topLineData: {
        totalUsers: undefined,
        activeUsers: undefined,
        percentage: undefined,
      },
      serverItemsLength: undefined,
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      headers: [
        { text: 'End user id', value: 'endUserId' },
        { text: 'Enrolled on', value: 'createdOn' },
        { text: 'Action', value: 'action' },
      ],
    }
  },
  created() {
    this.getUsers(0, 25)
    this.getSummaryData()
  },
  methods: {
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },
    async getSummaryData() {
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      const token = localStorage.getItem('token')
      try {
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/audience/top-bar`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Authorization: `${token}` },
        })
        if (response.ok) {
          const object = await response.json()
          this.topLineData.totalUsers = object.totalUsers
          this.topLineData.activeUsers = object.activeUsers
          this.topLineData.percentage = object.totalUsers
            ? (object.activeUsers / object.totalUsers) * 100
            : 0
        }
      } catch (error) {
        return
      }
    },
    onClearSearch() {
      this.search = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getUsers(0, this.serverOptions.rowsPerPage)
    },
    async getUsers(page, size) {
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      const token = localStorage.getItem('token')
      this.loading = true
      this.error = false
      try {
        const params = new URLSearchParams({ page, size })
        if (this.search) params.append('search', this.search)
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/end-user?${params.toString()}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `${token}` },
          }
        )
        if (response.ok) {
          const object = await response.json()
          this.users = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getUsers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
